import { Component, ChangeDetectionStrategy, ElementRef, Injector, OnDestroy, ViewEncapsulation, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { ImageModule } from 'primeng/image';
import type { UIMessage } from 'ai';

import { AgentUserMessageComponent } from '../user.message/user.message.component';
import { AgentAgentMessageComponent } from '../agent.message/agent.message.component';
import { AgentActivityBlockComponent } from '../activity.block/activity.block.component';
import { AgentMessageActionsComponent } from '../message.actions/message.actions.component';
import { agentMessageCopyText } from '../../agent.message.copy';
import { type ConfirmationCardField, type ConfirmationCardContextItem } from '../confirmation.card/confirmation.card.component';
import { buildToolTitle, buildConfirmationCardFields, buildConfirmationCardContext, extractToolReasoning } from '../confirmation.card/confirmation.card.view.model';
import { AgentFileDownloadListComponent } from '../file.download.list/file.download.list.component';
import { AgentBillScanCardComponent, type BillScanToolInput } from '../bill.scan.card/bill.scan.card.component';
import type { AIAgentAttachment, AIInterviewAnswer } from '@jms/model';
import { AlertComponent } from '@workbuddy/ui/components/alert';
import { Command } from '@workbuddy/ui/model';
import { AgentUtility } from '../../agent.utility';
import type { AICodeExecutionFile } from '@jms/model';
import type { AnyPart, ActivityBlockPart, CardAction, InterviewCardActionMeta, RenderedItem } from '../../agent.types';

// ─── Enriched view models ─────────────────────────────────────────────────────

interface EnrichedTextItem {
  kind: 'text';
  trackKey: string;
  part: AnyPart;
  partIndex: number;
  renderedText: string;
}

interface EnrichedFileItem {
  kind: 'file';
  trackKey: string;
  part: AnyPart;
  partIndex: number;
  fileName: string;
  mediaType: string;
  iconClass: string;
  url: string;
  isImage: boolean;
}

interface EnrichedSpecialToolItem {
  kind: 'special-tool';
  trackKey: string;
  part: AnyPart;
  partIndex: number;
  rawName: string;
  toolState: string;
  toolCallId: string;
  approvalId: string;
  displayName: string;
  fields: ConfirmationCardField[];
  context: ConfirmationCardContextItem[];
  reasoning: string;
  isInterview: boolean;
  /** Collapsed header text, e.g. "Answered 2 questions". */
  badgeSummary: string;
  /** One recap line per question: the question text paired with the chosen answer. */
  badgeLines: InterviewBadgeLine[];
}

interface InterviewBadgeLine {
  question: string;
  answer: string;
  skipped: boolean;
}

interface EnrichedActivityBlockItem {
  kind: 'activity-block';
  trackKey: string;
  parts: ActivityBlockPart[];
  blockIndex: number;
  blockKey: string;
}

interface EnrichedBillScanItem {
  kind: 'bill-scan';
  trackKey: string;
  part: AnyPart;
  partIndex: number;
  toolCallId: string;
  toolInput: BillScanToolInput;
}

interface EnrichedDownloadItem {
  kind: 'download';
  trackKey: string;
  files: AICodeExecutionFile[];
}

type EnrichedItem = EnrichedTextItem | EnrichedFileItem | EnrichedSpecialToolItem | EnrichedActivityBlockItem | EnrichedBillScanItem | EnrichedDownloadItem;

interface EnrichedMessage {
  message: UIMessage;
  items: EnrichedItem[];
  fileItems: EnrichedFileItem[];
  downloadFiles: AICodeExecutionFile[];
  /** What the copy action under the reply copies; empty when the reply has no text. */
  copyText: string;
}

@Component({
  selector: 'agent-message-list',
  standalone: true,
  imports: [ImageModule, AgentUserMessageComponent, AgentAgentMessageComponent, AgentActivityBlockComponent, AgentFileDownloadListComponent, AlertComponent, AgentBillScanCardComponent, AgentMessageActionsComponent],
  templateUrl: './message.list.component.html',
  styleUrls: ['./message.list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AgentMessageListComponent implements OnDestroy {

  private readonly injector = inject(Injector);

  public readonly $messages = input.required<UIMessage[]>({ alias: 'messages' });
  public readonly $isStreaming = input<boolean>(false, { alias: 'isStreaming' });
  public readonly $lastMessageIsUser = input<boolean>(false, { alias: 'lastMessageIsUser' });
  public readonly $revealedTextParts = input<Record<string, string>>({}, { alias: 'revealedTextParts' });
  public readonly $cardActions = input<Record<string, InterviewCardActionMeta>>({}, { alias: 'cardActions' });
  public readonly $blobUrls = input<Record<string, string>>({}, { alias: 'blobUrls' });
  public readonly $collapsedActivityBlocks = input<Set<string>>(new Set(), { alias: 'collapsedActivityBlocks' });
  public readonly $expandedTools = input<Set<string>>(new Set(), { alias: 'expandedTools' });
  public readonly $streamingError = input<string | null>(null, { alias: 'streamingError' });
  public readonly $threadLoadError = input<string | null>(null, { alias: 'threadLoadError' });

  public readonly cardActionComplete = output<{ toolCallId: string; action: CardAction }>();
  public readonly activityBlockToggled = output<string>();
  public readonly toolExpandToggled = output<string>();
  public readonly retry = output<void>();

  private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');
  /** Interview recaps are collapsed by default; tracks which ones the user expanded (keyed by toolCallId). */
  private readonly expandedInterviews = signal<Set<string>>(new Set());

  // Pixels of slack at the bottom — within this band the user is treated
  // as "at bottom" (auto-follow stays engaged, pill stays hidden). Picked
  // to absorb sub-pixel rounding.
  private static readonly AT_BOTTOM_PX = 50;

  // A user scroll must move the viewport UP by at least this many px to count as an intentional
  // scroll-up (releasing sticky follow). Filters sub-pixel jitter; the per-token auto-follow only
  // ever scrolls DOWN, so it never trips this.
  private static readonly SCROLL_UP_EPS = 4;

  protected readonly $showJumpPill = signal(false);

  // Resting spacer height — a little slack at the bottom of the conversation
  // so the last line never sits flush against the input.
  private static readonly RESTING_SPACER_PX = 12;

  // Phantom space below the last assistant response. While anchor mode is
  // active it is sized to exactly fill the viewport BELOW the pinned user
  // message — i.e. clientHeight minus the height of everything from the user
  // message down to the end of the real content. That is just enough to let
  // the user message reach the top, and no more: a flat viewport-tall spacer
  // would let the user scroll the whole conversation off-screen into blank
  // space. Collapses back to the resting height on stream end. Anchor mode is
  // "active" iff $anchorMessageId is set.
  protected readonly $spacerHeight = signal(AgentMessageListComponent.RESTING_SPACER_PX);

  // The user message currently pinned to the top of the viewport (anchor
  // mode). Null when not anchored. Kept so the spacer can be recomputed as
  // the reply streams in or the panel resizes.
  private anchorMessageId: string | null = null;

  // Sticky auto-follow: while true, every content-resize tick keeps the
  // viewport bottom-aligned with the latest content. Engaged on send (with
  // anchor), pill click, and when the user scrolls back to the bottom.
  // Released on scroll up.
  private isAutoFollowing = false;
  // Last observed scrollTop. onScroll uses it to tell an upward (user) scroll from a downward
  // (auto-follow) one, so a genuine scroll-up releases sticky follow even while tokens stream.
  private lastScrollTop = 0;
  // Guard so our own scrollTo calls don't trip onScroll's user-release
  // branch.
  private isProgrammaticScroll = false;
  // In-flight clear timer. Back-to-back auto-follow scrolls cancel the
  // previous timer so an earlier setTimeout doesn't drop the guard while a
  // later scroll's event is still pending.
  private programmaticScrollTimer?: number;
  private resizeObserver?: ResizeObserver;
  // Deferred spacer-collapse after the smooth end-of-stream settle. Tracked so
  // a new send/follow can cancel a pending collapse before it clobbers a fresh
  // anchor.
  private settleTimer?: number;

  constructor() {

    // Observe BOTH the scroll container (clientHeight changes on
    // popup↔pseudo expand) and the inner .msg-col (scrollHeight changes
    // as tokens stream in).
    //
    // Container resize: keep the spacer in sync with the new clientHeight
    // while anchor mode is active so the "viewport-worth of room beneath
    // the user message" UX survives panel size changes.
    //
    // Content resize: drive auto-follow. Target = bottom of REAL content
    // (excluding spacer). The "only scroll if target > current scrollTop"
    // check is what naturally bridges anchor → auto-follow: while content
    // fits within (anchor scrollTop + clientHeight) the target stays below
    // scrollTop and we don't move; once the response overflows, target
    // exceeds scrollTop and we begin following.
    effect(() => {
      const container = this.messagesContainer()?.nativeElement;
      this.resizeObserver?.disconnect();
      if (!container) return;

      const msgCol = container.querySelector('.msg-col');
      if (!msgCol) return;

      this.resizeObserver = new ResizeObserver((entries) => {
        const containerResized = entries.some(e => e.target === container);

        // While anchored, keep the spacer sized to exactly fill the viewport
        // below the pinned user message. As the reply streams in (content
        // resize) this shrinks the spacer; on panel resize (container resize)
        // it re-fits to the new clientHeight. Either way the user can never
        // scroll past the content into blank space.
        if (this.anchorMessageId) {
          this.updateAnchorSpacer(container);
          // A pure resize isn't new content to follow — re-fit and stop.
          if (containerResized) return;
        }

        if (this.isAutoFollowing) {
          const target = container.scrollHeight - this.$spacerHeight() - container.clientHeight;
          if (target > container.scrollTop) {
            // Deliberately NOT wrapped in programmaticScroll: during fast streaming the guard
            // re-arms every token and latches for the whole reply, swallowing the user's scroll-up.
            // onScroll tells this downward follow-scroll from a real user scroll by direction, so the
            // guard isn't needed here.
            this.scrollToBottomNow(container, 'instant');
          }
          return;
        }
        this.recomputePillVisibility();
      });
      this.resizeObserver.observe(container);
      this.resizeObserver.observe(msgCol);
    }, { injector: this.injector });
  }

  /**
   * Snap the scroll container so the bottom of REAL content (excluding the
   * spacer) sits at the bottom of the viewport. 'instant' bypasses the CSS
   * scroll-behavior:smooth — without it the resulting smooth-scroll events
   * fire after our programmaticScroll guard window has cleared and leak
   * through as user-scroll false positives.
   */
  private scrollToBottomNow(container: HTMLElement, behavior: ScrollBehavior): void {

    const target = Math.max(0, container.scrollHeight - this.$spacerHeight() - container.clientHeight);
    container.scrollTo({ top: target, behavior });
  }

  /**
   * Wraps a programmatic scroll mutation so onScroll's release branches treat
   * the resulting events as code-driven rather than user-driven.
   */
  private programmaticScroll(action: () => void, smooth = false): void {

    this.isProgrammaticScroll = true;
    // Cancel any pending clear from a prior call — without this, rapid
    // back-to-back scrolls (sticky auto-follow during streaming) hit a race:
    // call N's setTimeout fires at +50ms and clears the flag while call N+1's
    // scroll event is still in-flight, causing onScroll to read it as user
    // input and release the anchor.
    if (this.programmaticScrollTimer != null) clearTimeout(this.programmaticScrollTimer);
    action();
    // Clear after the browser has fired the resulting scroll event.
    // Smooth scrolls take ~300-400ms to settle; instant scrolls fire scroll
    // events asynchronously, so we use a small buffer (50ms) instead of 0
    // to avoid a race where the flag clears before the event arrives.
    const clearDelay = smooth ? 400 : 50;
    this.programmaticScrollTimer = window.setTimeout(() => {
      this.isProgrammaticScroll = false;
      this.programmaticScrollTimer = undefined;
    }, clearDelay);
  }

  /**
   * Pill shows when the user is scrolled away from the bottom AND
   * auto-follow is not currently driving the viewport. Called from
   * onScroll and from the content ResizeObserver (since content growth
   * during streaming doesn't itself fire a scroll event).
   */
  private recomputePillVisibility(): void {

    const container = this.messagesContainer()?.nativeElement;
    if (!container) return;
    // Content end = scrollHeight minus the bottom spacer. While anchored,
    // the spacer is a viewport-tall reservation that shouldn't count as
    // "missed content" for the pill check.
    const contentEnd = container.scrollHeight - this.$spacerHeight();
    const visibleBottom = container.scrollTop + container.clientHeight;
    const awayFromBottom = visibleBottom < contentEnd - AgentMessageListComponent.AT_BOTTOM_PX;
    const showPill = awayFromBottom && !this.isAutoFollowing;
    if (showPill !== this.$showJumpPill()) {
      this.$showJumpPill.set(showPill);
    }
  }

  public ngOnDestroy(): void {

    this.resizeObserver?.disconnect();
    if (this.programmaticScrollTimer != null) clearTimeout(this.programmaticScrollTimer);
    if (this.settleTimer != null) clearTimeout(this.settleTimer);
  }

  // ─── Computed ────────────────────────────────────────────────────────────

  protected readonly $conversationFiles = computed<AIAgentAttachment[]>(() => {

    const messages = this.$messages();
    const blobUrls = this.$blobUrls();
    const files: AIAgentAttachment[] = [];

    for (const message of messages) {
      if (message.role !== 'user') continue;
      for (const part of message.parts as AnyPart[]) {
        if (part.type !== 'file') continue;
        const filename = AgentUtility.resolveFilename(part);
        const url = AgentUtility.partField(part, 'url', '') || AgentUtility.partField(part, 'data', '');
        const mediaType = AgentUtility.partField(part, 'mediaType', '') || AgentUtility.partField(part, 'mimeType', '');
        // Use filename (includes att_{id}__ prefix) as ID, fallback to URL hash, then index
        const id = filename || url || `file-${files.length}`;
        files.push({ id, url, mediaType, filename, blobUrl: blobUrls[id] });
      }
    }
    return files;
  });

  protected readonly $retryActions = computed<Command[]>(() => {

    if (!this.$streamingError()) return [];
    return [new Command({ label: 'Retry', iconLeft: 'fa-regular fa-rotate-right', action: () => this.retry.emit() })];
  });

  protected readonly $lastMessage = computed<UIMessage | undefined>(() => {

    const msgs = this.$messages();
    return msgs.length > 0 ? msgs[msgs.length - 1] : undefined;
  });

  /** Pre-computed view models keyed by message ID — eliminates method calls in template loops */
  protected readonly $enrichedMessages = computed<Map<string, EnrichedMessage>>(() => {

    const messages = this.$messages();
    const revealedParts = this.$revealedTextParts();
    const cardActions = this.$cardActions();
    const result = new Map<string, EnrichedMessage>();

    for (const message of messages) {
      if (message.role !== 'assistant') continue;

      const grouped = AgentUtility.groupMessageParts(message.parts as AnyPart[]);
      const items: EnrichedItem[] = [];
      const fileItems: EnrichedFileItem[] = [];
      const seenFileIds = new Set<string>();
      const fallbackFiles: AICodeExecutionFile[] = [];

      for (const item of grouped) {
        switch (item.kind) {
          case 'text': {
            const key = `${message.id}-${item.partIndex}`;
            items.push({ kind: 'text', trackKey: `text-${item.partIndex}`, part: item.part, partIndex: item.partIndex, renderedText: revealedParts[key] ?? AgentUtility.partField(item.part, 'text', '') });
            break;
          }
          case 'file': {
            const mediaType = AgentUtility.partField(item.part, 'mediaType', '') || AgentUtility.partField(item.part, 'mimeType', '');
            // Check both 'url' and 'data' fields - Mastra V2 format stores URL in 'data', AIV5 uses 'url'
            const url = AgentUtility.partField(item.part, 'url', '') || AgentUtility.partField(item.part, 'data', '');
            const enriched: EnrichedFileItem = { kind: 'file', trackKey: `file-${item.partIndex}`, part: item.part, partIndex: item.partIndex, fileName: AgentUtility.displayFilename(item.part), mediaType, iconClass: AgentUtility.getFileIconClass(mediaType), url, isImage: AgentUtility.isImageType(mediaType) };
            items.push(enriched);
            fileItems.push(enriched);
            break;
          }
          case 'special-tool': {
            const rawName = AgentUtility.partField(item.part, 'toolName', '') || item.part.type.replace(/^tool-/, '');
            const toolCallId = AgentUtility.partField(item.part, 'toolCallId', '');
            const toolState = AgentUtility.partField<string>(item.part, 'state', '');
            const partInput = AgentUtility.partField<Record<string, unknown>>(item.part, 'input', {});
            fallbackFiles.push(...AgentUtility.collectDownloadFilesFromParts([item.part], seenFileIds));

            // Handle proposeBillScan separately as a dedicated card
            if (rawName === 'proposeBillScan' && toolState === 'output-available') {
              const outputData = AgentUtility.partField<Record<string, unknown>>(item.part, 'output', {});
              items.push({ kind: 'bill-scan', trackKey: `tool-${item.partIndex}`, part: item.part, partIndex: item.partIndex, toolCallId, toolInput: this.buildBillScanToolInput(outputData, partInput) });
              break;
            }

            const approval = AgentUtility.partField<{ id?: string } | undefined>(item.part, 'approval', undefined);
            const badgeLines = rawName === 'interview' ? this.buildInterviewBadgeLines(item.part, cardActions[toolCallId]) : [];
            const badgeSummary = this.buildInterviewSummary(badgeLines);
            items.push({ kind: 'special-tool', trackKey: `tool-${item.partIndex}`, part: item.part, partIndex: item.partIndex, rawName, toolState, toolCallId, approvalId: approval?.id ?? '', displayName: buildToolTitle(rawName, partInput), fields: buildConfirmationCardFields(rawName, partInput), context: buildConfirmationCardContext(rawName, partInput), reasoning: extractToolReasoning(rawName, partInput), isInterview: rawName === 'interview', badgeSummary, badgeLines });
            break;
          }
          case 'activity-block': {
            const blockKey = AgentUtility.activityBlockKey(message.id, item.blockIndex);
            items.push({ kind: 'activity-block', trackKey: `block-${item.blockIndex}`, parts: item.parts, blockIndex: item.blockIndex, blockKey });
            const blockFiles = AgentUtility.collectDownloadFilesFromParts(item.parts.map(part => part.part), seenFileIds);
            if (blockFiles.length) items.push({ kind: 'download', trackKey: `download-${item.blockIndex}`, files: blockFiles });
            break;
          }
        }
      }

      result.set(message.id, { message, items, fileItems, downloadFiles: fallbackFiles, copyText: agentMessageCopyText(grouped) });
    }

    return result;
  });

  // ─── Public Methods ──────────────────────────────────────────────────────

  protected isInterviewExpanded(toolCallId: string): boolean {

    return this.expandedInterviews().has(toolCallId);
  }

  protected toggleInterviewRecap(toolCallId: string): void {

    const next = new Set(this.expandedInterviews());
    if (next.has(toolCallId)) {
      next.delete(toolCallId);
    } else {
      next.add(toolCallId);
    }
    this.expandedInterviews.set(next);
  }

  /**
   * Engage sticky auto-follow and snap to bottom (no anchor). Called on
   * thread (re)load so a mid-stream response stays glued to the viewport
   * bottom on popup reopen.
   */
  public enableAutoFollow(): void {

    this.cancelSettle();
    this.isAutoFollowing = true;
    this.$showJumpPill.set(false);
    setTimeout(() => {
      const container = this.messagesContainer()?.nativeElement;
      if (!container) return;
      this.programmaticScroll(() => this.scrollToBottomNow(container, 'instant'));
    });
  }

  /**
   * Pin the supplied user message to the top of the viewport, reserve just
   * enough spacer below it to allow that (see updateAnchorSpacer), and engage
   * sticky auto-follow. The follow won't actually move scrollTop while the
   * assistant reply still fits below the pinned message — the user message
   * stays anchored and the reply grows into the shrinking spacer. Once the
   * reply overflows past the viewport the spacer bottoms out and the natural
   * transition into auto-follow begins.
   */
  public anchorUserMessageToTop(messageId: string): void {

    this.cancelSettle();
    this.isAutoFollowing = true;
    this.$showJumpPill.set(false);
    this.anchorMessageId = messageId;
    setTimeout(() => {
      const container = this.messagesContainer()?.nativeElement;
      if (!container) return;
      this.updateAnchorSpacer(container);
      requestAnimationFrame(() => {
        const target = container.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
        if (!target) return;
        this.programmaticScroll(() => target.scrollIntoView({ block: 'start', behavior: 'smooth' }), true);
      });
    });
  }

  /**
   * Size the bottom spacer to exactly fill the viewport below the pinned user
   * message: clientHeight minus the height of everything from the top of that
   * message to the end of the real content (the spacer itself excluded). This
   * is the minimum needed for the message to reach the top — capping it here
   * means the maximum scroll position lands with the user message at the top
   * and never past it, so the conversation can't be scrolled off into a blank
   * void. Floored at the resting height; shrinks toward it as the reply grows.
   */
  private updateAnchorSpacer(container: HTMLElement): void {

    const anchor = this.anchorMessageId
      ? container.querySelector<HTMLElement>(`[data-message-id="${this.anchorMessageId}"]`)
      : null;
    if (!anchor) return;

    const realContentHeight = container.scrollHeight - this.$spacerHeight();
    const anchorTop = anchor.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    const belowAnchor = realContentHeight - anchorTop;

    const spacer = Math.max(AgentMessageListComponent.RESTING_SPACER_PX, container.clientHeight - belowAnchor);
    this.$spacerHeight.set(spacer);
  }

  /**
   * Clear auto-follow and the anchor, collapsing the spacer to its resting
   * height. Called when the thread empties (new chat, switch) or when
   * streaming ends.
   *
   * On stream end the anchor spacer is often still tall (the reply was shorter
   * than the room reserved beneath the pinned user message). Collapsing it in
   * one frame makes the browser hard-clamp scrollTop, which reads as an
   * aggressive snap down to "reply flush at the bottom". To smooth that, we
   * first glide to the resting scroll position, then collapse the now
   * off-screen spacer once the scroll has settled.
   */
  public clearFollow(): void {

    this.cancelSettle();
    this.isAutoFollowing = false;
    this.anchorMessageId = null;
    this.$showJumpPill.set(false);

    const container = this.messagesContainer()?.nativeElement;
    if (!container) {
      this.$spacerHeight.set(AgentMessageListComponent.RESTING_SPACER_PX);
      return;
    }

    // Resting position once the spacer is gone: latest content flush at the
    // bottom (or the natural top if it all already fits). Measured against the
    // real content height, excluding the current spacer.
    const realContentHeight = container.scrollHeight - this.$spacerHeight();
    const restTarget = Math.max(0, realContentHeight + AgentMessageListComponent.RESTING_SPACER_PX - container.clientHeight);

    // Already at/below the resting point (e.g. a long reply we were following):
    // nothing to glide, just collapse the spacer.
    if (restTarget >= container.scrollTop) {
      this.$spacerHeight.set(AgentMessageListComponent.RESTING_SPACER_PX);
      setTimeout(() => this.recomputePillVisibility(), 0);
      return;
    }

    // Glide down to the resting position, then collapse the off-screen spacer
    // after the smooth scroll settles so it produces no extra clamp.
    this.programmaticScroll(() => container.scrollTo({ top: restTarget, behavior: 'smooth' }), true);
    this.settleTimer = window.setTimeout(() => {
      this.settleTimer = undefined;
      this.$spacerHeight.set(AgentMessageListComponent.RESTING_SPACER_PX);
      this.recomputePillVisibility();
    }, 400);
  }

  /** Cancel a pending end-of-stream spacer collapse, if any. */
  private cancelSettle(): void {

    if (this.settleTimer != null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = undefined;
    }
  }

  protected onScroll(event: Event): void {

    const el = event.target as HTMLElement;
    // "At bottom" = bottom of REAL content within tolerance — ignore the
    // anchor spacer's reserved room so scrolling into the spacer below the
    // last token still counts as "at bottom" (not a release intent).
    const contentEnd = el.scrollHeight - this.$spacerHeight();
    const atBottom = el.scrollTop + el.clientHeight >= contentEnd - AgentMessageListComponent.AT_BOTTOM_PX;
    // Direction is the reliable signal: the per-token auto-follow only ever scrolls DOWN, so any
    // upward move is the user. Releasing on a genuine scroll-up (rather than a bare atBottom check)
    // means the follow's own downward scrolls — which can lag behind fast content growth and briefly
    // read as "not at bottom" — never false-release, while the user's scroll-up always wins.
    const movedUp = el.scrollTop < this.lastScrollTop - AgentMessageListComponent.SCROLL_UP_EPS;
    this.lastScrollTop = el.scrollTop;

    // A guarded programmatic scroll (end-of-stream settle / anchor) may move up — don't let it toggle
    // follow. Otherwise: a real scroll-up releases, and reaching the bottom re-engages live follow.
    if (!this.isProgrammaticScroll) {
      if (movedUp) {
        this.isAutoFollowing = false;
      } else if (atBottom) {
        this.isAutoFollowing = true;
      }
    }

    // Don't flash the pill while a programmatic scroll is in flight (e.g. the
    // end-of-stream settle glides through "away from bottom" before landing).
    const showPill = !atBottom && !this.isAutoFollowing && !this.isProgrammaticScroll;
    if (showPill !== this.$showJumpPill()) {
      this.$showJumpPill.set(showPill);
    }
  }

  protected onJumpToBottom(): void {

    const container = this.messagesContainer()?.nativeElement;
    if (!container) return;
    this.isAutoFollowing = true;
    this.$showJumpPill.set(false);
    this.programmaticScroll(() => this.scrollToBottomNow(container, 'smooth'), true);
  }

  // ─── Template Helpers ────────────────────────────────────────────────────

  /**
   * Builds the answered-interview recap badge: one line per question pairing the
   * question text with the chosen answer. The authoritative source is the tool
   * part's own `output` ({ questions, answers }) — it carries the full question
   * text and survives the Mastra round-trip, so live and restored threads render
   * identically. The live `$cardActions` entry is only a fallback for the brief
   * window before the resumed tool output lands (it lacks question text, so we
   * fall back to positional "Question N" labels there).
   */
  private buildInterviewBadgeLines(part: AnyPart, cardAction: InterviewCardActionMeta | undefined): InterviewBadgeLine[] {

    const output = AgentUtility.resolveOutput(part);
    const rawQuestions = output?.['questions'];
    const rawAnswers = output?.['answers'];
    const questions = Array.isArray(rawQuestions) ? rawQuestions as Array<Record<string, unknown>> : [];
    const outputAnswers = Array.isArray(rawAnswers) ? rawAnswers as AIInterviewAnswer[] : [];

    if (questions.length) {
      return questions.map((question, i) => {
        const text = String(question?.['question'] ?? `Question ${i + 1}`);
        const answer = outputAnswers.find(a => a.questionIndex === i) ?? outputAnswers[i];
        return this.toBadgeLine(text, answer);
      });
    }

    // Fallback: live just-submitted state with no output yet — positional labels only.
    if (cardAction?.answers?.length) {
      return cardAction.answers.map((answer, i) => this.toBadgeLine(`Question ${i + 1}`, answer));
    }

    return [];
  }

  /** Maps a question/answer pair into a recap line, flagging skipped answers. */
  private toBadgeLine(question: string, answer: AIInterviewAnswer | undefined): InterviewBadgeLine {

    const skipped = !answer || answer.skipped;
    if (skipped) return { question, answer: 'Skipped', skipped: true };
    const values = [...(answer.selectedLabels ?? [])];
    if (answer.customText) values.push(`"${answer.customText}"`);
    return values.length ? { question, answer: values.join(', '), skipped: false } : { question, answer: 'Skipped', skipped: true };
  }

  /** Collapsed header text for the interview recap, e.g. "Answered 2 questions". */
  private buildInterviewSummary(lines: InterviewBadgeLine[]): string {

    if (!lines.length) return '';
    const noun = lines.length === 1 ? 'question' : 'questions';
    const answered = lines.some(line => !line.skipped);
    return `${answered ? 'Answered' : 'Skipped'} ${lines.length} ${noun}`;
  }

  protected onCardAction(toolCallId: string, action: CardAction): void {

    this.cardActionComplete.emit({ toolCallId, action });
  }

  private buildBillScanToolInput(output: Record<string, unknown>, input: Record<string, unknown>): BillScanToolInput {

    return {
      fileIndexes: (output['fileIndexes'] ?? input['fileIndexes'] ?? []) as number[],
      attachmentIds: (output['attachmentIds'] ?? input['attachmentIds']) as string[] | undefined,
      jobId: (output['jobId'] ?? input['jobId'] ?? '') as string,
      stageId: (output['stageId'] ?? input['stageId'] ?? '') as string,
      jobName: (output['jobName'] ?? input['jobName']) as string | undefined,
      stageName: (output['stageName'] ?? input['stageName']) as string | undefined,
    };
  }

  protected getCardActionStatus(toolCallId: string): 'success' | 'declined' | undefined {

    const action = this.$cardActions()[toolCallId]?.action;
    if (!action) return undefined;
    if (action === 'approved') return 'success';
    if (action === 'declined') return 'declined';
    return undefined;
  }

  protected onBillScanComplete(toolCallId: string, event: { success: boolean; message: string }): void {

    this.cardActionComplete.emit({ toolCallId, action: event.success ? 'approved' : 'declined' });
  }

  protected onBillScanDecline(toolCallId: string): void {

    this.cardActionComplete.emit({ toolCallId, action: 'declined' });
  }

  // ─── Error Helpers ───────────────────────────────────────────────────────

  protected friendlyErrorMessage(error: string): string {

    if (error.includes('fetch failed') || error.includes('Failed to fetch') || error.includes('NetworkError')) {
      return 'Could not reach the assistant. Please check your connection and try again.';
    }
    if (error.includes('Type validation failed') || error.includes('invalid_union')) {
      return 'Could not reach the assistant. The server may be temporarily unavailable.';
    }
    if (error.includes('Tool result is missing')) {
      return 'A previous action was interrupted. Please try your request again.';
    }
    if (error.length > 150) {
      return 'Something went wrong. Please try again.';
    }
    return error;
  }
}
